import {
  FormLabel,
  Stack,
  TextField,
  Typography,
  FormControl,
  type SxProps,
} from '@mui/material';
import { useState } from 'react';
import type React from 'react';

import { HelpIcon } from '@/components/shared/helpIcon';

import type { FieldValidation } from './validation/fieldValidation';
import { InputErrorIcon } from './validation/inputErrorIcon';

export const FormTextField = ({
  size = 'medium',
  testId,
  title,
  helpText,
  alwaysShowHelp = false,
  dataTestId,
  validation,
  placeholder,
  value,
  onChange,
  sx,
  disabled = false,
  autoFocus = false,
  multiline = false,
  rows = 1,
  maxLength,
}: {
  size?: 'small' | 'medium';
  testId?: string;
  title: React.ReactNode;
  helpText?: React.ReactNode;
  alwaysShowHelp?: boolean;
  dataTestId?: string;
  validation?: FieldValidation;
  placeholder: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sx?: SxProps;
  disabled?: boolean;
  autoFocus?: boolean;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
}) => {
  const [touched, setTouched] = useState(false);
  const hasError =
    touched && (validation ? !validation.validateInput(value) : false);

  return (
    <FormControl className='group/help' sx={{ width: '100%', ...sx }}>
      {title && (
        <FormLabel>
          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant='subtitle1'>{title}</Typography>
            {helpText && (
              <HelpIcon
                title={helpText}
                alwaysVisible={alwaysShowHelp}
                className='text-(--mui-palette-text-secondary)'
              />
            )}
          </Stack>
        </FormLabel>
      )}
      <TextField
        multiline={multiline}
        rows={rows}
        size={size}
        autoFocus={autoFocus}
        data-testid={testId}
        onBlur={() => setTouched(true)}
        error={hasError}
        sx={{
          '& .MuiInputBase-root': {
            paddingRight: hasError ? '4px' : '24px',
          },
          ...(multiline && {
            '& .MuiOutlinedInput-input': {
              paddingLeft: '0px',
            },
          }),
        }}
        slotProps={{
          htmlInput: {
            ...(dataTestId ? { 'data-testid': dataTestId } : {}),
            ...(maxLength !== undefined ? { maxLength } : {}),
          },
          input: {
            endAdornment: hasError && (
              <InputErrorIcon errorMessage={validation?.errorMessage(value)} />
            ),
          },
        }}
        placeholder={placeholder}
        disabled={disabled}
        value={value}
        onChange={onChange}
      />
    </FormControl>
  );
};
